// Drives the sy-support-chat widget from host UI (see StreamsterSupportChat for
// the injection side). The widget publishes window.SupportChat once it mounts.
//
// That "once it mounts" is the whole reason these helpers exist rather than
// callers poking window.SupportChat directly: the widget is injected only for
// authenticated, non-white-label users, and even then it loads asynchronously —
// it can wait up to IDENTITY_TIMEOUT_MS for the identity call before the script
// tag is even appended. A link that silently does nothing because the user was
// quick is worse than one that opens the old ticket form, so every helper falls
// back to the public ticket page.

// Fallback surface when the widget isn't up yet: the same support chat as a
// standalone full-screen page. It replaces the old Freshdesk ticket form at
// help.streamster.io, which is not where support lives any more.
//
// The page is anonymous — the identity token lives in the SPA, not in this new
// tab — so the ticket form asks for an email the widget would have known. That
// is the accepted cost of a fallback that only fires when the widget is absent.
const CHAT_FALLBACK_URL = 'https://support.streamster.io/chat.html?projectId=streamster';
// ?ticket=1 opens the ticket form directly instead of the AI chat.
const TICKET_FALLBACK_URL = `${CHAT_FALLBACK_URL}&ticket=1`;

interface SupportChatApi {
  open?: () => void;
  openTicket?: (options?: { subject?: string; description?: string; intro?: string }) => void;
}

const getApi = (): SupportChatApi | undefined =>
  typeof window === 'undefined'
    ? undefined
    : (window as unknown as { SupportChat?: SupportChatApi }).SupportChat;

const openFallback = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

/** Expands the chat widget on the AI conversation. */
export const openSupportChat = () => {
  const open = getApi()?.open;

  if (typeof open === 'function') {
    open();
    return;
  }

  openFallback(CHAT_FALLBACK_URL);
};

/**
 * Expands the widget straight into its ticket form, skipping the AI
 * conversation — for links that already say "submit a ticket".
 */
export const openSupportTicket = (options?: { subject?: string; description?: string }) => {
  const openTicket = getApi()?.openTicket;

  if (typeof openTicket === 'function') {
    openTicket(options);
    return;
  }

  // The prefill survives the hop to the standalone page — see src/embed/chat.tsx
  // in sy-support-chat — so a "report this payment problem" link still arrives
  // with its subject and description filled in.
  const params = new URLSearchParams();
  if (options?.subject) {
    params.set('ticketSubject', options.subject);
  }
  if (options?.description) {
    params.set('ticketDescription', options.description);
  }
  const query = params.toString();

  openFallback(query ? `${TICKET_FALLBACK_URL}&${query}` : TICKET_FALLBACK_URL);
};
